import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExerciseSearchFilters } from "./components/ExerciseSearchFilters";
import { ExerciseLibraryGrid } from "./components/ExerciseLibraryGrid";
import { MultiSelectForm } from "./components/MultiSelectForm";
import { ExerciseFilters, getExercises } from "@/lib/storage";
import { Exercise } from "./types/ExerciseTypes";
import { Card, CardContent } from "@/components/ui/card";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslations } from "@/hooks/useTranslations";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/components/LanguageContext";

interface ExerciseSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectExercise?: (exercise: Exercise) => void;
  onSelectMultipleExercises?: (exercises: Exercise[], defaultSets: number, defaultReps: string, defaultTime?: string) => void;
  currentDayExercises?: Exercise[];
}

export type ExerciseFromDB = {
  id: number | string;
  name: string;
  body_part: string;
  equipment?: string | null;
  target_muscle?: string | null;
  gif_url?: string | null;
  difficulty?: string | null;
  rating?: number | null;
  [key: string]: any;
};

export type SelectedExercise = ExerciseFromDB & {
  isSelected: boolean;
  isCustom?: boolean;
};

export function ExerciseSelectionDialog({
  open,
  onOpenChange,
  onSelectExercise,
  onSelectMultipleExercises,
  currentDayExercises = []
}: ExerciseSelectionDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [exercises, setExercises] = useState<SelectedExercise[]>([]);
  const [loading, setLoading] = useState(false);
  const [bodyPart, setBodyPart] = useState<string>("_all");
  const [equipment, setEquipment] = useState<string>("_all");
  const [targetMuscle, setTargetMuscle] = useState<string>("_all");
  const [selectedExercises, setSelectedExercises] = useState<SelectedExercise[]>([]);
  const [uniqueBodyParts, setUniqueBodyParts] = useState<string[]>([]);
  const [uniqueEquipment, setUniqueEquipment] = useState<string[]>([]);
  const [uniqueTargetMuscles, setUniqueTargetMuscles] = useState<string[]>([]);
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslations("workouts");
  const { language } = useLanguage();
  const { user } = useAuth();

  const multiSelectMode = true;

  useEffect(() => {
    if (!open) {
      return;
    }
    
    loadExercises().then(() => {
      if (currentDayExercises && currentDayExercises.length > 0) {
        findAndSelectCurrentExercises();
      }
    });
  }, [open, currentDayExercises]);

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      loadExercises().then(() => {
        if (currentDayExercises && currentDayExercises.length > 0) {
          findAndSelectCurrentExercises();
        }
      });
    }
  }, [bodyPart, equipment, targetMuscle, showCustomOnly, open]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (open) {
        setCurrentPage(1);
        loadExercises().then(() => {
          if (currentDayExercises && currentDayExercises.length > 0) {
            findAndSelectCurrentExercises();
          }
        });
      }
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, open]);

  const findAndSelectCurrentExercises = () => {
    if (!currentDayExercises || !exercises.length) return;
    
    const exerciseNamesToFind = currentDayExercises.map(ex => ex.name?.toLowerCase()).filter(Boolean);
    
    if (exerciseNamesToFind.length === 0) return;
    
    console.log("Procurando exercícios com os seguintes nomes:", exerciseNamesToFind);
    
    const matchingExercises = exercises.filter(ex => 
      ex.name && exerciseNamesToFind.includes(ex.name.toLowerCase())
    );
    
    console.log(`Encontrados ${matchingExercises.length} exercícios correspondentes`);
    
    if (matchingExercises.length > 0) {
      setSelectedExercises(matchingExercises);
      
      setExercises(prevExercises => 
        prevExercises.map(ex => ({
          ...ex,
          isSelected: matchingExercises.some(match => String(match.id) === String(ex.id))
        }))
      );
    }
  };

  const loadExercises = async () => {
    try {
      setLoading(true);
      const filters: ExerciseFilters = {
        searchTerm,
        bodyPart: bodyPart !== "_all" ? bodyPart : undefined,
        equipment: equipment !== "_all" ? equipment : undefined,
        targetMuscle: targetMuscle !== "_all" ? targetMuscle : undefined
      };
      
      let data = await getExercises(language, filters) as ExerciseFromDB[];

      if (user?.id) {
        const { data: custom, error } = await supabase
          .from("custom_exercises")
          .select("*")
          .or(`trainer_id.eq.${user.id}`);
        
        if (custom) {
          const mappedCustom: SelectedExercise[] = custom.map(ex => ({
            ...ex,
            isCustom: true,
            id: String(ex.id),
            isSelected: false
          }));

          if (showCustomOnly) {
            data = mappedCustom;
          } else {
            data = [...data, ...mappedCustom];
          }
        }
      }

      const exerciseNames = currentDayExercises?.map(ex => ex.name?.toLowerCase()).filter(Boolean) || [];
      
      const mappedExercises: SelectedExercise[] = data.map(ex => {
        const isInCurrentDay = ex.name && exerciseNames.includes(ex.name.toLowerCase());
        const existingSelectedEx = selectedExercises.find(e => String(e.id) === String(ex.id));
        
        return {
          ...ex,
          id: String(ex.id),
          isSelected: isInCurrentDay || !!existingSelectedEx
        };
      });
      
      setExercises(mappedExercises);
      
      const newSelectedExercises = mappedExercises.filter(ex => ex.isSelected);
      if (newSelectedExercises.length > 0) {
        setSelectedExercises(newSelectedExercises);
      }
      
      if (uniqueBodyParts.length === 0) {
        const allExercises = await getExercises(language);
        const bodyParts = Array.from(new Set(allExercises.map(e => e.body_part))).filter(Boolean).sort();
        setUniqueBodyParts(bodyParts);
        const equipmentList = Array.from(new Set(allExercises.map(e => e.equipment))).filter(Boolean).sort();
        setUniqueEquipment(equipmentList);
        const targetMuscles = Array.from(new Set(allExercises.map(e => e.target_muscle))).filter(Boolean).sort();
        setUniqueTargetMuscles(targetMuscles);
      }
    } catch (error) {
      console.error('Erro ao carregar exercícios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExercise = (exercise: SelectedExercise) => {
    const isSelected = selectedExercises.some(ex => String(ex.id) === String(exercise.id));
    
    if (isSelected) {
      setSelectedExercises(prev => prev.filter(ex => String(ex.id) !== String(exercise.id)));
    } else {
      setSelectedExercises(prev => [...prev, exercise]);
    }
    
    setExercises(prevExercises => 
      prevExercises.map(ex => 
        String(ex.id) === String(exercise.id) 
          ? { ...ex, isSelected: !isSelected } 
          : ex
      )
    );
  };

  const handleBodyPartChange = (value: string) => {
    setBodyPart(value);
    setEquipment("_all");
    setTargetMuscle("_all");
  };

  const removeSelectedExercise = (exerciseId: number | string) => {
    setSelectedExercises(prev => prev.filter(ex => String(ex.id) !== String(exerciseId)));
    
    setExercises(prevExercises => 
      prevExercises.map(ex => 
        String(ex.id) === String(exerciseId) 
          ? { ...ex, isSelected: false } 
          : ex
      )
    );
  };

  const handleAddExercises = (defaultSets: number, defaultReps: string, defaultTime?: string) => {
    if (!onSelectMultipleExercises || selectedExercises.length === 0) return;

    const formattedExercises = selectedExercises.map(ex => {
      const exercise: Exercise = {
        name: ex.name,
        sets: defaultSets,
        reps: defaultReps,
        time: defaultTime,
        image_url: ex.isCustom ? ex.image_url : ex.gif_url,
        body_part: ex.body_part,
        target_muscle: ex.target_muscle || undefined,
        instructions: ex.instructions || undefined,
        isCustom: ex.isCustom
      };
      return exercise;
    });
    
    onSelectMultipleExercises(formattedExercises, defaultSets, defaultReps, defaultTime);
    onOpenChange(false);
  };

  const filterProps = {
    searchTerm,
    setSearchTerm,
    bodyPart,
    equipment,
    targetMuscle,
    setEquipment,
    setTargetMuscle,
    uniqueBodyParts,
    uniqueEquipment,
    uniqueTargetMuscles,
    handleBodyPartChange,
    showCustomOnly,
    onShowCustomChange: setShowCustomOnly
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] mt-20 overflow-y-auto my-[27px]">
        <DialogHeader>
          <DialogTitle>{t("exerciseLibraryButton")}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          {selectedExercises.length > 0 && <MultiSelectForm selectedCount={selectedExercises.length} onAddExercises={handleAddExercises} />}
          
          {selectedExercises.length > 0 && (
            <Card className="rounded-xl overflow-hidden dark:bg-gray-800/80">
              <CardContent className="p-4 dark:bg-gray-700/30">
                <h3 className="font-medium mb-2 dark:text-white">{selectedExercises.length} {t("exercises.selectedCount").replace("{{count}}", selectedExercises.length.toString())}</h3>
                <ScrollArea className="h-24">
                  <div className="flex flex-wrap gap-2">
                    {selectedExercises.map((ex, index) => (
                      <div key={ex.id} className="flex items-center bg-slate-50 dark:bg-gray-600 rounded-xl p-1.5 pr-3 border border-slate-100 dark:border-gray-500 shadow-sm">
                        <div className="w-10 h-10 mr-2 relative">
                          <div className="absolute -top-1 -left-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold z-10 shadow-sm">
                            {index + 1}
                          </div>
                          <ExerciseImage 
                            imagePath={ex.gif_url || ex.image_url || ""} 
                            altText={ex.name} 
                            fallbackText={ex.name}
                            className="rounded-md overflow-hidden"
                            isCustomExercise={ex.isCustom}
                          />
                        </div>
                        <span className="text-sm mr-2 max-w-32 truncate dark:text-white">{ex.name}</span>
                        <button 
                          type="button" 
                          className="text-slate-400 dark:text-slate-200 hover:text-destructive dark:hover:text-destructive rounded-full hover:bg-slate-100 dark:hover:bg-gray-500 p-1"
                          onClick={() => removeSelectedExercise(ex.id)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
          
          <ExerciseSearchFilters {...filterProps} />
          
          <ExerciseLibraryGrid 
            exercises={exercises} 
            loading={loading} 
            multiSelectMode={multiSelectMode} 
            onSelectExercise={handleSelectExercise} 
            selectedExercises={selectedExercises}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}


